import { statisticMessages } from 'src/features/statistic/utils/messages';
import { PAYMENT_TYPE } from 'src/features/payment/utils/types.ts';

export type PaymentByMethodStatistic = {
    method_name: PAYMENT_TYPE;
    total_amount: number;
    transactions_count: number;
    average_amount: string;
};

export type PaymentByMachineStatistic = {
    machine_name: string;
    total_amount: number;
    transactions_count: number;
    average_amount: string;
};

export type PaymentByProductStatistic = {
    product_type: string;
    total_amount: number;
    transactions_count: number;
    average_amount: string;
};

type PaymentTotals = {
    total_amount: number;
    transactions_count: number;
    average_amount: number;
    methods_count: number;
    products_count: number;
    payment_machines_count: number;
};

export type PaymentStatistic = {
    totals: PaymentTotals;
    payments_by_method: PaymentByMethodStatistic[];
    payments_by_payment_machine: PaymentByMachineStatistic[];
    payments_by_product: PaymentByProductStatistic[];
};

export type MonthlyPaymentStatistic = {
    totals: PaymentTotals;
    payments_by_method: {
        day: number;
        bank_payments_count: number;
        card_payments_count: number;
        cash_payments_count: number;
        online_payments_count: number;
    }[];
    payments_by_payment_machine: {
        day: number;
        [key: string]: number;
    }[];
};

export type VisitHour = {
    client: number;
    member: number;
    reservation: number;
    subscription: number;
};

export type VisitStatisticHourlyBreakdown = {
    from_00_to_01: VisitHour;
    from_01_to_02: VisitHour;
    from_02_to_03: VisitHour;
    from_03_to_04: VisitHour;
    from_04_to_05: VisitHour;
    from_05_to_06: VisitHour;
    from_06_to_07: VisitHour;
    from_07_to_08: VisitHour;
    from_08_to_09: VisitHour;
    from_09_to_10: VisitHour;
    from_10_to_11: VisitHour;
    from_11_to_12: VisitHour;
    from_12_to_13: VisitHour;
    from_13_to_14: VisitHour;
    from_14_to_15: VisitHour;
    from_15_to_16: VisitHour;
    from_16_to_17: VisitHour;
    from_17_to_18: VisitHour;
    from_18_to_19: VisitHour;
    from_19_to_20: VisitHour;
    from_20_to_21: VisitHour;
    from_21_to_22: VisitHour;
    from_22_to_23: VisitHour;
    from_23_to_00: VisitHour;
};

export type VisitStatisticHourlyDurationBreakdown = {
    under_15m: VisitHour;
    between_15m_and_30m: VisitHour;
    between_30m_and_1h: VisitHour;
    between_1h_and_2h: VisitHour;
    between_2h_and_3h: VisitHour;
    between_3h_and_4h: VisitHour;
    between_4h_and_5h: VisitHour;
    between_5h_and_6h: VisitHour;
    between_6h_and_7h: VisitHour;
    between_7h_and_8h: VisitHour;
    between_8h_and_9h: VisitHour;
    more_than_9h: VisitHour;
};

export enum VisitStatisticTotalsKey {
    CLIENT_VISITS_COUNT = 'client_visits_count',
    MEMBER_VISITS_COUNT = 'member_visits_count',
    RESERVATION_VISITS_COUNT = 'reservation_visits_count',
    SUBSCRIPTION_VISITS_COUNT = 'subscription_visits_count',
}

export const TOTAL_VISITS_KEY = 'total_visits_count';

export type VisitStatisticTotals = {
    [TOTAL_VISITS_KEY]: number;
    [VisitStatisticTotalsKey.CLIENT_VISITS_COUNT]: number;
    [VisitStatisticTotalsKey.MEMBER_VISITS_COUNT]: number;
    [VisitStatisticTotalsKey.RESERVATION_VISITS_COUNT]: number;
    [VisitStatisticTotalsKey.SUBSCRIPTION_VISITS_COUNT]: number;
};

export const VisitTranslationMap = {
    [VisitStatisticTotalsKey.CLIENT_VISITS_COUNT]: statisticMessages.clients,
    [VisitStatisticTotalsKey.MEMBER_VISITS_COUNT]: statisticMessages.members,
    [VisitStatisticTotalsKey.RESERVATION_VISITS_COUNT]: statisticMessages.reservations,
    [VisitStatisticTotalsKey.SUBSCRIPTION_VISITS_COUNT]: statisticMessages.subscriptions,
};

export type VisitStatistic = {
    total_visits_section: VisitStatisticTotals;
    duration_breakdown_section: VisitStatisticHourlyDurationBreakdown;
    hourly_breakdown_section: VisitStatisticHourlyBreakdown;
};

export type DataKey = {
    title: string;
    value: string;
};

export type TransformedBarItem = {
    title: string;
    [key: string]: string | number;
};

export enum VisitorType {
    CLIENT = 'client',
    MEMBER = 'member',
    RESERVATION = 'reservation',
    SUBSCRIPTION = 'subscription',
}

export type CapacityAverageDuration = {
    visitor_type: VisitorType;
    avg_duration_seconds: number;
    total_visitors_count: number;
};

export type CapacityStatistics = {
    pie_charts_section_stats: {
        total_visits_pie_chart: VisitStatisticTotals;
        visits_until_time_pie_chart: VisitStatisticTotals;
        capacity_at_time_pie_chart: {
            free_client_spaces_count: number;
            free_member_spaces_count: number;
            free_reservation_spaces_count: number;
            free_subscription_spaces_count: number;
            total_spaces_count: number;
            total_free_spaces_count: number;
        };
    };
    visits_breakdown_for_day_stats: CapacityAverageDuration[];
    visits_breakdown_for_time_stats: CapacityAverageDuration[];
    capacity_hourly_breakdown_for_day_stats: VisitStatisticHourlyBreakdown;
};

type GatewayPass = {
    gateway_pass_allowed: number,
    gateway_pass_not_allowed: number
};

export type GatewayBarSection = {
    from_00_to_01: GatewayPass;
    from_01_to_02: GatewayPass;
    from_02_to_03: GatewayPass;
    from_03_to_04: GatewayPass;
    from_04_to_05: GatewayPass;
    from_05_to_06: GatewayPass;
    from_06_to_07: GatewayPass;
    from_07_to_08: GatewayPass;
    from_08_to_09: GatewayPass;
    from_09_to_10: GatewayPass;
    from_10_to_11: GatewayPass;
    from_11_to_12: GatewayPass;
    from_12_to_13: GatewayPass;
    from_13_to_14: GatewayPass;
    from_14_to_15: GatewayPass;
    from_15_to_16: GatewayPass;
    from_16_to_17: GatewayPass;
    from_17_to_18: GatewayPass;
    from_18_to_19: GatewayPass;
    from_19_to_20: GatewayPass;
    from_20_to_21: GatewayPass;
    from_21_to_22: GatewayPass;
    from_22_to_23: GatewayPass;
    from_23_to_00: GatewayPass;
};

export type GatewayStatistics = {
    metrics_section_stats: {
        total_passes_count: number;
        failed_passes_count: number;
        detections_count: number;
        vehicles_per_minute_count: number;
    };
    bars_section_stats: GatewayBarSection;
};
